import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Edit,
  Trash2,
  FileText,
  Save,
  X,
  Eye,
  Copy,
  MessageSquare,
  Bell,
  Users,
  Search
} from 'lucide-react';
import { whatsappService } from '../../../../services/whatsappService';

const MessageTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const messageTypes = [
    { value: 'appointment_confirmation', label: 'Appointment Confirmation', icon: MessageSquare, color: 'text-blue-400' },
    { value: 'appointment_reminder', label: 'Appointment Reminder', icon: Bell, color: 'text-yellow-400' },
    { value: 'custom', label: 'Custom Message', icon: FileText, color: 'text-purple-400' },
    { value: 'bulk', label: 'Bulk Campaign', icon: Users, color: 'text-green-400' }
  ];

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await whatsappService?.getMessageTemplates();
      if (!error) {
        setTemplates(data || []);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates?.filter(template => {
    const matchesSearch = template?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      template?.template_text?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesType = selectedType === 'all' || template?.message_type === selectedType;
    return matchesSearch && matchesType;
  });

  const handleCreateTemplate = () => {
    setEditingTemplate({
      name: '',
      message_type: 'custom',
      template_text: '',
      variables: {},
      is_active: true
    });
    setShowCreateModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({ ...template });
    setShowCreateModal(true);
  };

  const handleSaveTemplate = async () => {
    try {
      if (editingTemplate?.id) {
        await whatsappService?.updateMessageTemplate(editingTemplate?.id, editingTemplate);
      } else {
        await whatsappService?.createMessageTemplate(editingTemplate);
      }
      await loadTemplates();
      setShowCreateModal(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await whatsappService?.deleteMessageTemplate(id);
        await loadTemplates();
      } catch (error) {
        console.error('Error deleting template:', error);
      }
    }
  };

  const copyTemplate = async (template) => {
    try {
      await navigator.clipboard?.writeText(template?.template_text);
      // You could show a toast notification here
    } catch (error) {
      console.error('Failed to copy template:', error);
    }
  };

  const extractVariables = (text) => {
    const matches = text?.match(/\{\{([^}]+)\}\}/g);
    return matches ? matches?.map(match => match?.replace(/[{}]/g, '')) : [];
  };

  const previewTemplateWithSampleData = (template) => {
    let preview = template?.template_text;
    const sampleData = {
      patient_name: 'John Doe',
      doctor_name: 'Dr. Smith',
      appointment_date: '2025-10-26',
      appointment_time: '10:00 AM',
      clinic_name: 'Ayurveda Wellness Center',
      phone: '+1234567890'
    };

    Object.keys(sampleData)?.forEach(key => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      preview = preview?.replace(regex, sampleData?.[key]);
    });

    return preview;
  };

  const getTypeIcon = (type) => {
    const typeInfo = messageTypes?.find(t => t?.value === type);
    return typeInfo ? typeInfo?.icon : FileText;
  };

  const getTypeColor = (type) => {
    const typeInfo = messageTypes?.find(t => t?.value === type);
    return typeInfo ? typeInfo?.color : 'text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-400/30 border-t-blue-400 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-purple-500/20 text-purple-400">
            <FileText className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Message Templates</h2>
            <p className="text-gray-400">Create and manage WhatsApp message templates</p>
          </div>
        </div>

        <button
          onClick={handleCreateTemplate}
          className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Create Template</span>
        </button>
      </div>
      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>

            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e?.target?.value)}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            >
              <option value="all">All Types</option>
              {messageTypes?.map(type => (
                <option key={type?.value} value={type?.value}>{type?.label}</option>
              ))}
            </select>
          </div>

          <div className="text-sm text-gray-400">
            {filteredTemplates?.length} template{filteredTemplates?.length !== 1 ? 's' : ''} found
          </div>
        </div>
      </motion.div>
      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredTemplates?.map((template) => {
          const TypeIcon = getTypeIcon(template?.message_type);
          return (
            <motion.div
              key={template?.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:border-white/30 transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg bg-white/5 ${getTypeColor(template?.message_type)}`}>
                    <TypeIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{template?.name}</h3>
                    <p className="text-sm text-gray-400 capitalize">
                      {template?.message_type?.replace('_', ' ')}
                    </p>
                  </div>
                </div>

                <div className={`px-2 py-1 rounded-full text-xs font-medium ${template?.is_active
                    ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                  }`}>
                  {template?.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>
              <div className="mb-4">
                <div className="text-sm text-gray-300 line-clamp-3 mb-2">
                  {template?.template_text}
                </div>

                {extractVariables(template?.template_text)?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {extractVariables(template?.template_text)?.map((variable, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-md"
                      >
                        {variable}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                <span>Created: {new Date(template.created_at)?.toLocaleDateString()}</span>
                <span>By: {template?.created_by_profile?.full_name || 'System'}</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPreviewTemplate(template)}
                  className="flex-1 px-3 py-2 bg-white/5 hover:bg-white/10 text-white text-sm rounded-lg transition-colors flex items-center justify-center space-x-1"
                >
                  <Eye className="w-4 h-4" />
                  <span>Preview</span>
                </button>

                <button
                  onClick={() => copyTemplate(template)}
                  className="px-3 py-2 bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>

                <button
                  onClick={() => handleEditTemplate(template)}
                  className="px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>

                <button
                  onClick={() => handleDeleteTemplate(template?.id)}
                  className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>
      {filteredTemplates?.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-300 mb-2">No templates found</h3>
          <p className="text-gray-400 mb-6">Create your first message template to get started</p>
          <button
            onClick={handleCreateTemplate}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors"
          >
            Create Template
          </button>
        </div>
      )}
      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showCreateModal && editingTemplate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-gray-900 rounded-2xl border border-white/20 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">
                    {editingTemplate?.id ? 'Edit Template' : 'Create New Template'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingTemplate(null);
                    }}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Template Name
                    </label>
                    <input
                      type="text"
                      value={editingTemplate?.name}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      placeholder="Enter template name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Message Type
                    </label>
                    <select
                      value={editingTemplate?.message_type}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, message_type: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    >
                      {messageTypes?.map(type => (
                        <option key={type?.value} value={type?.value}>{type?.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Message Template
                  </label>
                  <textarea
                    value={editingTemplate?.template_text}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, template_text: e?.target?.value })}
                    rows={6}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    placeholder="Enter your message template. Use {{variable_name}} for dynamic content."
                  />

                  <div className="mt-2 text-sm text-gray-400">
                    Available variables: patient_name, doctor_name, appointment_date, appointment_time, clinic_name
                  </div>
                </div>

                <div>
                  <label className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={editingTemplate?.is_active}
                      onChange={(e) => setEditingTemplate({ ...editingTemplate, is_active: e?.target?.checked })}
                      className="w-5 h-5 text-blue-500 bg-white/5 border-white/20 rounded focus:ring-blue-500/50"
                    />
                    <span className="text-gray-300">Template is active</span>
                  </label>
                </div>

                {/* Preview */}
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <h4 className="text-sm font-medium text-gray-300 mb-2">Preview with Sample Data:</h4>
                  <div className="text-white">
                    {previewTemplateWithSampleData(editingTemplate)}
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-white/10 flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingTemplate(null);
                  }}
                  className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors flex items-center space-x-2"
                >
                  <Save className="w-5 h-5" />
                  <span>Save Template</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Preview Modal */}
      <AnimatePresence>
        {previewTemplate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-gray-900 rounded-2xl border border-white/20 w-full max-w-lg"
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Template Preview</h3>
                  <button
                    onClick={() => setPreviewTemplate(null)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <h4 className="font-semibold text-white mb-2">{previewTemplate?.name}</h4>
                  <div className="text-sm text-gray-400 capitalize">
                    {previewTemplate?.message_type?.replace('_', ' ')}
                  </div>
                </div>

                <div className="bg-green-600 text-white p-4 rounded-2xl rounded-bl-none max-w-xs">
                  {previewTemplateWithSampleData(previewTemplate)}
                </div>

                <div className="mt-4 text-xs text-gray-500">
                  Preview with sample data
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MessageTemplates;